import {
  Alert,
  Container,
  LinearProgress,
  Box,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import React from "react";
import useCommonGetInit from "../../hooks/useCommonGetInit";
import { API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse } from "../../utils/util";
import {
  Business,
  Person,
  Email,
  Phone,
  LocationOn,
  Language,
  Category,
  Event,
} from "@mui/icons-material";
import useApiEp from "../../hooks/useApiEp";

const ExhibitorHallStand = ({ hall, stand }) => {
  const { data, isLoading, isError, error } = useApiEp(
    `/admin/get-exhibitor-by-hall-stand?hallNumber=${hall}&standNumber=${stand}`
  );

  if (isLoading) {
    return <LinearProgress />;
  }

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No exhibitor data found</Alert>;
  }

  const exhibitor = data;

  const Section = ({ title, icon, children }) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        {icon}
        <Typography
          variant="subtitle1"
          sx={{ ml: 1, fontWeight: "bold", fontSize: "0.9rem" }}
        >
          {title}
        </Typography>
      </Box>
      {children}
      <Divider sx={{ mt: 1 }} />
    </Box>
  );

  const InfoRow = ({ label, value }) =>
    value && (
      <Box sx={{ mb: 0.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: "medium", fontSize: "0.7rem" }}
        >
          {label}:
        </Typography>
        <Typography variant="body2" sx={{ ml: 0.5, fontSize: "0.8rem" }}>
          {value}
        </Typography>
      </Box>
    );

  return (
    <Container maxWidth="sm" sx={{ py: 1, px: 1 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 2, pt: 1 }}>
        <Typography
          variant="h6"
          component="h1"
          sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
        >
          {exhibitor.companyName?.value}
        </Typography>
        {exhibitor.companyShortName?.value && (
          <Typography variant="caption" color="text.secondary">
            {exhibitor.companyShortName.value}
          </Typography>
        )}
        <Chip
          label={exhibitor.fascia?.value}
          size="small"
          sx={{ mt: 0.5, height: 24, fontSize: "0.7rem" }}
        />
      </Box>

      {/* Company Info */}
      <Section title="Company" icon={<Business sx={{ fontSize: 18 }} />}>
        <InfoRow label="We Are" value={exhibitor.weAre?.value} />
        <InfoRow label="Category" value={exhibitor.exhibitorCategory?.value} />
        {exhibitor.website?.value && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            <Language sx={{ fontSize: 16, mr: 0.5 }} />
            <Typography
              variant="body2"
              component="a"
              href={`https://${exhibitor.website.value}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: "0.8rem",
                color: "primary.main",
                textDecoration: "none",
              }}
            >
              {exhibitor.website.value}
            </Typography>
          </Box>
        )}
      </Section>

      {/* Contact Info */}
      <Section title="Contact" icon={<Person sx={{ fontSize: 18 }} />}>
        <InfoRow
          label="Contact"
          value={`${exhibitor.contactSalutation?.value} ${exhibitor.contactFirstName?.value} ${exhibitor.contactLastName?.value}`.trim()}
        />
        <InfoRow label="Role" value={exhibitor.contactDesignation?.value} />
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          <Email sx={{ fontSize: 16, mr: 0.5 }} />
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            {exhibitor.contactEmail?.value || exhibitor.companyEmail?.value}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Phone sx={{ fontSize: 16, mr: 0.5 }} />
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            {exhibitor.mobile?.value || exhibitor.phone?.value}
          </Typography>
        </Box>
      </Section>

      {/* Address */}
      <Section title="Address" icon={<LocationOn sx={{ fontSize: 18 }} />}>
        <Typography variant="body2" sx={{ fontSize: "0.8rem", mb: 0.5 }}>
          {[
            exhibitor.address1?.value,
            exhibitor.address2?.value,
            exhibitor.address3?.value,
          ]
            .filter(Boolean)
            .join(", ")}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
          {exhibitor.city?.value?.name}, {exhibitor.state?.value?.name}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
          {exhibitor.pin?.value} • {exhibitor.country?.value?.name}
        </Typography>
      </Section>

      {/* Exhibition Details */}
      <Section title="Exhibition" icon={<Event sx={{ fontSize: 18 }} />}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <InfoRow label="Hall" value={exhibitor.hallNumber?.value} />
          <InfoRow label="Stand" value={exhibitor.standNumber?.value} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <InfoRow label="Space" value={exhibitor.spaceType?.value} />
          <InfoRow
            label="Size"
            value={
              exhibitor.standSize?.value ? `${exhibitor.standSize.value}m²` : ""
            }
          />
        </Box>
      </Section>

      {/* Schedule */}
      <Section title="Schedule">
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <InfoRow
            label="Arrival"
            value={
              exhibitor.exhibitArrival?.value
                ? new Date(exhibitor.exhibitArrival.value).toLocaleDateString()
                : ""
            }
          />
          <InfoRow
            label="Dismantle"
            value={
              exhibitor.dismantling?.value
                ? new Date(exhibitor.dismantling.value).toLocaleDateString()
                : ""
            }
          />
        </Box>
      </Section>

      {/* Product Categories */}
      {/* <Section title="Products" icon={<Category sx={{ fontSize: 18 }} />}>
        <Box sx={{ maxHeight: 120, overflow: "auto" }}>
          {exhibitor.productCategories?.value?.map((category, index) => (
            <Box
              key={index}
              sx={{ mb: 0.5, p: 0.5, bgcolor: "grey.50", borderRadius: 0.5 }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", fontSize: "0.65rem" }}
              >
                {category.mainCat.split(" - ")[0]}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.6rem", display: "block" }}
              >
                {category.subSubCat}
              </Typography>
            </Box>
          ))}
        </Box>
      </Section> */}
    </Container>
  );
};

export default ExhibitorHallStand;
